import PageSeo from '@/components/feature/PageSeo';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { contentPrinciples, site, stats, testimonials } from '@/mocks/site';
import ContactForm from './components/ContactForm';

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 糖心吃瓜网',
    url: 'https://renrenmingxiao.cn/about',
    inLanguage: 'zh-CN',
    description: site.description,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    url: 'https://renrenmingxiao.cn/',
    email: site.email,
    description: site.description,
    foundingDate: '2023',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系我们 - 糖心吃瓜网',
    url: 'https://renrenmingxiao.cn/about',
    inLanguage: 'zh-CN',
  },
];

const milestones = [
  {
    year: '2023',
    title: '从一张截图开始',
    desc: '最初只是几个朋友互相转发好笑的截图，后来攒得太多，干脆搭了个页面统一放。',
  },
  {
    year: '2024',
    title: '专栏开始成形',
    desc: '热瓜、段子、沙雕图慢慢分了家，每个专栏有了自己的更新节奏和固定的读者。',
  },
  {
    year: '2025',
    title: '把投稿交给你们',
    desc: '开放投稿通道后，站内超过一半的内容来自网友分享，编辑只做筛选和整理。',
  },
  {
    year: '2026',
    title: '每天 20:00 的约定',
    desc: '固定的更新时间和一封每日精选简讯，让刷糖心吃瓜网变成很多人的下班仪式。',
  },
];

export default function AboutPage() {
  return (
    <>
      <PageSeo
        title="关于我们 - 糖心吃瓜网品牌介绍与内容原则"
        description="了解糖心吃瓜网的品牌定位、发展理念与内容原则。我们专注收集打工人饭点与摸鱼时刻的快乐碎片，坚持好笑但不刻薄的内容标准。"
        keywords="糖心吃瓜网,关于我们,糖心吃瓜,内容原则,投稿"
        canonical="https://renrenmingxiao.cn/about"
        jsonLd={aboutJsonLd}
      />
      <ScrollToTop />
      <SiteHeader />
      <Breadcrumb items={[{ label: '关于我们' }]} />

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cream%20and%20amber%20abstract%20illustration%20of%20a%20cozy%20shared%20table%20with%20teacups%20popcorn%20and%20rounded%20geometric%20shapes%2C%20soft%20warm%20gradient%20background%20with%20gentle%20shadows%20for%20text%20contrast%2C%20minimal%20editorial%20style%2C%20no%20text%2C%20calm%20friendly%20mood&width=1800&height=800&seq=chigua-about-01&orientation=landscape"
              alt="糖心吃瓜网品牌介绍配图 - 温暖的分享场景"
              title="关于糖心吃瓜网"
              className="h-full w-full object-top object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/65 to-foreground-950/40" />
          </div>

          <div className="relative mx-auto w-full max-w-[1400px] px-4 py-16 md:px-6 md:py-24">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-100/25 bg-background-50/10 px-3.5 py-1.5 text-xs text-background-100 backdrop-blur">
              关于我们
            </span>
            <h1 className="mt-5 max-w-3xl font-heading text-3xl leading-tight text-background-50 md:text-5xl">
              糖心吃瓜网：认真收集每一点小小的快乐
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              我们不是新闻网站，也不是段子工厂。我们只是把散落在通勤、午休、加班和深夜里的那些好笑瞬间，
              整理成一个随时可以点开的地方。
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6 border-t border-background-100/15 pt-8 md:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <p className="font-heading text-2xl text-primary-300 md:text-3xl">
                    {item.value}
                    <span className="ml-1 text-sm text-background-300">{item.unit}</span>
                  </p>
                  <p className="mt-1 text-xs text-background-300 md:text-sm">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-16">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                <i className="ri-compass-3-line w-3.5 h-3.5 flex items-center justify-center" />
                POSITIONING
              </span>
              <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
                我们的定位
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  糖心吃瓜网面向的是那些想在碎片时间里乐呵两分钟的人。可能是等电梯的一分钟，
                  也可能是外卖还没到的那十分钟。
                </p>
                <p>
                  所以我们在选内容的时候有一个很朴素的判断标准：它能不能让一个刚开完会的人，
                  在三十秒内笑出来。能，就留下；不能，就再等等。
                </p>
                <p>
                  我们不追求信息量最大，只希望每一次点开都不算浪费时间。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
                <h3 className="font-heading text-lg text-foreground-950">一路走来</h3>
                <ol className="mt-6 space-y-6">
                  {milestones.map((item, index) => (
                    <li key={item.year} className="relative flex gap-4 pl-1">
                      <div className="flex flex-col items-center">
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                            index === milestones.length - 1
                              ? 'bg-accent-500 text-background-50'
                              : 'bg-primary-100 text-primary-700'
                          }`}
                        >
                          {index + 1}
                        </span>
                        {index < milestones.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-background-300" />
                        )}
                      </div>
                      <div className="pb-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                            {item.year}
                          </span>
                          <h4 className="text-sm font-medium text-foreground-900 md:text-base">
                            {item.title}
                          </h4>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                          {item.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="bg-background-100/80 py-14 md:py-16">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-secondary-700">
                <i className="ri-shield-check-line w-3.5 h-3.5 flex items-center justify-center" />
                PRINCIPLES
              </span>
              <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
                内容原则
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                这四条是我们做内容时反复拿出来对一遍的标准，也是很多读者愿意留下来的原因。
              </p>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {contentPrinciples.map((item, index) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6"
                >
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                      index % 3 === 0
                        ? 'bg-primary-100 text-primary-700'
                        : index % 3 === 1
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-secondary-100 text-secondary-800'
                    }`}
                  >
                    <i className={`${item.icon} w-6 h-6 flex items-center justify-center text-2xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="reviews" className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-16">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-accent-600">
              <i className="ri-chat-smile-2-line w-3.5 h-3.5 flex items-center justify-center" />
              VOICES
            </span>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
              读者怎么说
            </h2>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {testimonials.map((item, index) => (
              <figure
                key={item.name}
                className={`rounded-2xl border p-6 ${
                  index % 2 === 0
                    ? 'border-primary-200/70 bg-primary-50/60'
                    : 'border-background-200/70 bg-background-50'
                }`}
              >
                <blockquote className="text-sm leading-relaxed text-foreground-700">
                  「{item.text}」
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3 text-sm">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 font-heading text-foreground-800">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block font-medium text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact" className="bg-background-100/80 py-14 md:py-16">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6">
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                  <i className="ri-mail-line w-3.5 h-3.5 flex items-center justify-center" />
                  CONTACT
                </span>
                <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
                  联系我们 / 我要投稿
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  有好笑的瓜、想纠错、想做点什么合作，都可以写在这里。
                  被采用的投稿我们会署名展示，优质内容还会给一点小小的稿费。
                </p>

                <dl className="mt-8 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                      <i className="ri-mail-send-line w-5 h-5 flex items-center justify-center text-xl" />
                    </span>
                    <div>
                      <dt className="text-sm text-foreground-500">投稿与联系邮箱</dt>
                      <dd>
                        <a
                          href="mailto:hello@tangxin-chigua.example.com"
                          className="break-all text-sm font-medium text-foreground-900 transition-colors hover:text-primary-700"
                        >
                          hello@tangxin-chigua.example.com
                        </a>
                      </dd>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-800">
                      <i className="ri-time-line w-5 h-5 flex items-center justify-center text-xl" />
                    </span>
                    <div>
                      <dt className="text-sm text-foreground-500">回复时间</dt>
                      <dd className="text-sm font-medium text-foreground-900">
                        工作日 10:00 - 19:00，通常一个工作日内回复
                      </dd>
                    </div>
                  </div>
                </dl>
              </div>

              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </>
  );
}